import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useAuth } from "hooks/useAuth";
import Loading from "components/Loading";
import AiDisclosureNotice from "shared/components/aiDisclosure/AiDisclosureNotice";
import TciSecureLinkConsentScreen from "components/cmr/TciSecureLinkConsentScreen";
import { db } from "services/firebase";
import { fetchCmrWithInboundBridge } from "services/cmr/cmrInboundBridge";
import { tciRequiresSecureLinkConsent } from "services/cmr/tciSessionConsent";
import {
  formatCmrFieldLabel,
  getActiveCmrDataFlow,
} from "constants/cmrFieldRegistry";
import {
  createConversationSessionId,
  persistChartMindPlusModuleCompletion,
} from "./chartMindPlusSessionService";

const STEPS = {
  BOOT: "boot",
  CONSENT: "consent",
  LOAD_CMR: "load_cmr",
  CONFIRM: "confirm",
  DONE: "done",
};

const LANDING_PATH = "/chartmind-plus";

export function formatConfirmFieldValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function buildDefaultValues(fieldIds) {
  return Object.fromEntries((fieldIds || []).map((id) => [id, ""]));
}

/**
 * Shared ChartMind-Plus module session: consent → gated CMR read → confirm.
 * Boot loads tciRegistry/{tciId} and cmrDataFlow only (no Plus enablement list).
 */
export default function ChartMindPlusModuleSession({
  tciId,
  title,
  expectedFieldIds = [],
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isPatientSurface = !location.pathname.startsWith("/dashboard");
  const { user, userData, userLoading, organizationId } = useAuth();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;

  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = createConversationSessionId();
  }
  const tciSessionId = sessionIdRef.current;

  const [step, setStep] = useState(STEPS.BOOT);
  const [bootError, setBootError] = useState("");
  const [tciDoc, setTciDoc] = useState(null);
  const [flowConfigured, setFlowConfigured] = useState(false);
  const [fieldIds, setFieldIds] = useState(expectedFieldIds);
  const [cmrValues, setCmrValues] = useState({});
  const [cmrLoadError, setCmrLoadError] = useState("");
  const [consented, setConsented] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  const { control, handleSubmit, reset } = useForm({
    defaultValues: buildDefaultValues(expectedFieldIds),
  });

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      if (userLoading || (user?.uid && userData === undefined)) return;
      if (!user?.uid) {
        setBootError(`Sign in to start ${title}.`);
        return;
      }
      if (!institutionId) {
        setBootError("No institution is linked to your account.");
        return;
      }

      try {
        const snap = await db.collection("tciRegistry").doc(tciId).get();
        if (!snap.exists) {
          if (!cancelled) setBootError(`${title} TCI was not found.`);
          return;
        }
        const doc = { id: snap.id, ...snap.data() };
        const flow = getActiveCmrDataFlow(doc);
        const configured = (flow.inbound?.length || 0) > 0;
        const ids = configured
          ? flow.inbound.map((row) => row.field)
          : expectedFieldIds;

        if (!cancelled) {
          setTciDoc(doc);
          setFlowConfigured(configured);
          setFieldIds(ids);
          reset(buildDefaultValues(ids));
          if (configured && tciRequiresSecureLinkConsent(doc)) {
            setStep(STEPS.CONSENT);
          } else {
            // No active inbound cmrDataFlow yet (owner has not run activation) —
            // or no consent topics. Do not call the CMR bridge.
            setConsented(false);
            setCmrValues({});
            setStep(STEPS.CONFIRM);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setBootError(err?.message || `Failed to start ${title}.`);
        }
      }
    }

    boot();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, userData, userLoading, institutionId, tciId, title, expectedFieldIds, reset]);

  const loadCmr = useCallback(async () => {
    setStep(STEPS.LOAD_CMR);
    setCmrLoadError("");
    try {
      const context = await fetchCmrWithInboundBridge({
        tciId,
        patientUid: user.uid,
        tciSessionId,
        readerUid: user.uid,
      });
      const values = {};
      const formDefaults = {};
      fieldIds.forEach((fieldId) => {
        const raw = context?.[fieldId];
        values[fieldId] = raw;
        formDefaults[fieldId] = formatConfirmFieldValue(raw);
      });
      setCmrValues(values);
      reset(formDefaults);
      setStep(STEPS.CONFIRM);
    } catch (err) {
      setCmrLoadError(err?.message || "Failed to load MasterChart data.");
      setCmrValues({});
      reset(buildDefaultValues(fieldIds));
      setStep(STEPS.CONFIRM);
    }
  }, [user?.uid, tciSessionId, tciId, fieldIds, reset]);

  const handleConsented = useCallback(() => {
    setConsented(true);
    loadCmr();
  }, [loadCmr]);

  const handleDeclined = useCallback(() => {
    setConsented(false);
    setCmrValues({});
    reset(buildDefaultValues(fieldIds));
    setStep(STEPS.CONFIRM);
  }, [reset, fieldIds]);

  const onConfirm = handleSubmit(async (formValues) => {
    if (!user?.uid || !institutionId) return;
    setSaving(true);
    setSaveError("");
    setSaveSuccess("");
    try {
      const confirmedValues = {};
      const corrections = {};
      fieldIds.forEach((fieldId) => {
        const confirmed = String(formValues[fieldId] ?? "").trim();
        confirmedValues[fieldId] = confirmed || null;
        const cmrRaw = formatConfirmFieldValue(cmrValues[fieldId]).trim();
        if (consented && cmrRaw && confirmed && confirmed !== cmrRaw) {
          corrections[fieldId] = confirmed;
        }
      });

      const serializableCmr = {};
      fieldIds.forEach((fieldId) => {
        serializableCmr[fieldId] =
          cmrValues[fieldId] === undefined ? null : cmrValues[fieldId];
      });

      await persistChartMindPlusModuleCompletion({
        patientUid: user.uid,
        sessionId: tciSessionId,
        organizationId: institutionId,
        institutionId,
        tciId,
        moduleLabel: title,
        cmrValues: serializableCmr,
        confirmedValues,
        corrections,
      });

      setSaveSuccess(`${title} completed.`);
      setStep(STEPS.DONE);
    } catch (err) {
      setSaveError(err?.message || `Failed to save ${title}.`);
    } finally {
      setSaving(false);
    }
  });

  const header = useMemo(
    () => (
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ChartMind-Plus module
        </Typography>
        {isPatientSurface ? (
          <Box sx={{ mt: 2, textAlign: "left" }}>
            <AiDisclosureNotice setting="remote" variant="banner" locale="en" />
          </Box>
        ) : null}
      </Box>
    ),
    [title, isPatientSurface],
  );

  if (!user) {
    return (
      <Box sx={{ maxWidth: 560, mx: "auto", py: 4, px: 2 }}>
        {header}
        <Alert severity="info">Sign in to start {title}.</Alert>
        <Button component={RouterLink} to={LANDING_PATH} sx={{ mt: 2 }}>
          Back to ChartMind-Plus
        </Button>
      </Box>
    );
  }

  if (step === STEPS.BOOT) {
    if (bootError) {
      return (
        <Box sx={{ maxWidth: 560, mx: "auto", py: 4, px: 2 }}>
          {header}
          <Alert severity="warning">{bootError}</Alert>
          <Button component={RouterLink} to={LANDING_PATH} sx={{ mt: 2 }}>
            Back to ChartMind-Plus
          </Button>
        </Box>
      );
    }
    return <Loading page />;
  }

  if (step === STEPS.CONSENT) {
    return (
      <Box sx={{ maxWidth: 560, mx: "auto", py: 4, px: 2 }}>
        {header}
        <TciSecureLinkConsentScreen
          tciDoc={tciDoc}
          tciId={tciId}
          tciSessionId={tciSessionId}
          tciDisplayName={tciDoc?.name || title}
          onConsented={handleConsented}
          onDeclined={handleDeclined}
        />
        <Button component={RouterLink} to={LANDING_PATH} sx={{ mt: 2 }}>
          Cancel
        </Button>
      </Box>
    );
  }

  if (step === STEPS.LOAD_CMR) {
    return (
      <Box
        sx={{
          maxWidth: 560,
          mx: "auto",
          py: 4,
          px: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {header}
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading MasterChart data…
        </Typography>
      </Box>
    );
  }

  if (step === STEPS.DONE) {
    return (
      <Box sx={{ maxWidth: 560, mx: "auto", py: 4, px: 2 }}>
        {header}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {saveSuccess}
          </Alert>
        )}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {title} is complete for this session.
        </Typography>
        <Button variant="contained" onClick={() => navigate(LANDING_PATH)}>
          Back to ChartMind-Plus
        </Button>
      </Box>
    );
  }

  const hasAnyCmr = fieldIds.some(
    (id) => formatConfirmFieldValue(cmrValues[id]).trim() !== "",
  );

  return (
    <Box sx={{ maxWidth: 560, mx: "auto", py: 4, px: 2 }}>
      {header}

      {!flowConfigured && (
        <Alert severity="info" sx={{ mb: 2, textAlign: "left" }}>
          This module isn&apos;t fully configured yet — MasterChart data-flow for{" "}
          {title} has not been activated. You can still enter session values
          below; they will not update MasterChart.
        </Alert>
      )}

      {flowConfigured && !consented && (
        <Alert severity="info" sx={{ mb: 2, textAlign: "left" }}>
          You continued without MasterChart access. Enter values for this session
          only — they will not update your MasterChart record.
        </Alert>
      )}

      {flowConfigured && consented && hasAnyCmr && (
        <Alert severity="info" sx={{ mb: 2, textAlign: "left" }}>
          Confirm the MasterChart values below, or correct them for this session.
          Corrections are saved for your care team and do not overwrite MasterChart
          in this step.
        </Alert>
      )}

      {flowConfigured && consented && !hasAnyCmr && (
        <Alert severity="info" sx={{ mb: 2, textAlign: "left" }}>
          No MasterChart values were returned for this module&apos;s topics. Enter
          values for this session (MasterChart will not be updated here).
        </Alert>
      )}

      {cmrLoadError && (
        <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
          {cmrLoadError}
        </Alert>
      )}
      {saveError && (
        <Alert
          severity="error"
          sx={{ mb: 2, textAlign: "left" }}
          onClose={() => setSaveError("")}
        >
          {saveError}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={onConfirm}
        noValidate
        sx={{ textAlign: "left" }}
      >
        {fieldIds.map((fieldId) => {
          const cmrDisplay = formatConfirmFieldValue(cmrValues[fieldId]);
          const multiline =
            cmrDisplay.includes("\n") ||
            fieldId === "medications" ||
            fieldId === "allergies" ||
            fieldId === "pmh" ||
            fieldId === "psh";
          return (
            <Box key={fieldId} sx={{ mb: 1 }}>
              {consented && cmrDisplay ? (
                <Typography variant="caption" color="text.secondary">
                  On file:{" "}
                  {cmrDisplay.length > 120
                    ? `${cmrDisplay.slice(0, 120)}…`
                    : cmrDisplay}
                </Typography>
              ) : null}
              <Controller
                name={fieldId}
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={formatCmrFieldLabel(fieldId)}
                    fullWidth
                    margin="normal"
                    multiline={multiline}
                    minRows={multiline ? 3 : 1}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message || "Confirm or correct"}
                  />
                )}
              />
            </Box>
          );
        })}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
          <LoadingButton type="submit" variant="contained" loading={saving}>
            Confirm and complete
          </LoadingButton>
          <Button component={RouterLink} to={LANDING_PATH} disabled={saving}>
            Cancel
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

ChartMindPlusModuleSession.propTypes = {
  tciId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  expectedFieldIds: PropTypes.arrayOf(PropTypes.string),
};
